// SiblingPropagateModal — Feb 24, 2026 (Randy pt89c-xxii, Phase 0).
// Opens after the user saves an edit in EventEditorModal, IF future
// non-Google siblings with the same title exist. Offers deterministic
// (non-LLM) propagation modes: shift_by, new_pattern, biweekly,
// monthly, every_n_days, custom. All math, all reversible for 15 min
// via the shared /undo endpoint.
import { useEffect, useMemo, useState } from "react";
import { X, ArrowsClockwise, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const DOW_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DOW_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Compute a date shift in days between two ISO dates (b - a).
function daysBetween(aIso, bIso) {
  if (!aIso || !bIso) return 0;
  const a = new Date(aIso + "T00:00:00Z");
  const b = new Date(bIso + "T00:00:00Z");
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}
function prettyDate(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, {
    weekday: "short", month: "short", day: "numeric", timeZone: "UTC",
  });
}

export default function SiblingPropagateModal({ siloId, eventId, priorEvent, newEvent, onClose, onApplied }) {
  // priorEvent + newEvent are BOTH plain event dicts (before-edit + after-edit).
  const [loading, setLoading] = useState(true);
  const [siblings, setSiblings] = useState([]);
  const [anchorDow, setAnchorDow] = useState(null);
  const [mode, setMode] = useState("just_this");
  const [nDays, setNDays] = useState(14);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Which axes to propagate. Auto-checked based on delta.
  const [applyTime, setApplyTime] = useState(false);
  const [applyAssignee, setApplyAssignee] = useState(false);
  const [applyDuration, setApplyDuration] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/calendar/${siloId}/events/${eventId}/siblings-by-title`);
        if (!alive) return;
        setSiblings(r.data?.siblings || []);
        setAnchorDow(r.data?.anchor_dow);
      } catch (e) {
        if (alive) setResult({ kind: "err", msg: e.response?.data?.detail || "Couldn't scan for siblings." });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [siloId, eventId]);

  // Compute detected delta from priorEvent → newEvent (helps default the toggles).
  const delta = useMemo(() => {
    if (!priorEvent || !newEvent) return {};
    return {
      dateShift: daysBetween(priorEvent.start_date, newEvent.start_date),
      timeChanged: (priorEvent.start_time || null) !== (newEvent.start_time || null),
      durationChanged: (priorEvent.end_time || null) !== (newEvent.end_time || null),
      assigneeChanged: (priorEvent.assignee_user_id || null) !== (newEvent.assignee_user_id || null),
    };
  }, [priorEvent, newEvent]);

  useEffect(() => {
    if (delta.timeChanged) setApplyTime(true);
    if (delta.durationChanged) setApplyDuration(true);
    if (delta.assigneeChanged) setApplyAssignee(true);
    if (delta.dateShift) setMode("shift_by");
  }, [delta.timeChanged, delta.durationChanged, delta.assigneeChanged, delta.dateShift]);

  const title = newEvent?.title || priorEvent?.title || "this event";
  const count = siblings.length;
  const oldDowName = priorEvent?.start_date ? DOW_NAMES[new Date(priorEvent.start_date + "T00:00:00Z").getUTCDay() === 0 ? 6 : new Date(priorEvent.start_date + "T00:00:00Z").getUTCDay() - 1] : "";
  const newDowName = newEvent?.start_date ? DOW_NAMES[new Date(newEvent.start_date + "T00:00:00Z").getUTCDay() === 0 ? 6 : new Date(newEvent.start_date + "T00:00:00Z").getUTCDay() - 1] : "";

  const apply = async () => {
    setBusy(true);
    try {
      const payload = {
        mode,
        date_shift_days: delta.dateShift || 0,
        n_days: nDays,
        date_from: dateFrom || null,
        date_to: dateTo || null,
        apply_time: applyTime,
        apply_assignee: applyAssignee,
        apply_duration: applyDuration,
      };
      const r = await api.post(`/calendar/${siloId}/events/${eventId}/propagate`, payload);
      setResult({
        kind: "ok",
        msg: r.data?.summary || `Applied to ${r.data?.modified || 0} · deleted ${r.data?.deleted || 0}.`,
        jobId: r.data?.job_id,
        modified: r.data?.modified,
        deleted: r.data?.deleted,
        undoTtl: r.data?.undo_ttl_minutes || 15,
      });
      onApplied?.({ jobId: r.data?.job_id, summary: r.data?.summary, modified: r.data?.modified, deleted: r.data?.deleted, undoTtl: r.data?.undo_ttl_minutes || 15 });
    } catch (e) {
      setResult({ kind: "err", msg: e.response?.data?.detail || "Couldn't apply." });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[210] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="modal-safe bg-white rounded-2xl p-6 text-sm text-[#6B6A68]" data-testid="sibling-propagate-loading">Scanning for matching future events…</div>
      </div>
    );
  }

  // No siblings, or only Google siblings — dismiss with no prompt.
  if (count === 0) {
    return (
      <div className="fixed inset-0 z-[210] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
        <div className="modal-safe bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="sibling-propagate-none">
          <div className="text-sm text-[#1C1B1A] mb-3">
            <CheckCircle size={16} weight="duotone" className="inline text-[#4A9E6A] mr-1.5" />
            No other future <strong>{title}</strong> events to update.
          </div>
          <button onClick={onClose} className="w-full text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-white hover:bg-black">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[210] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={busy ? undefined : onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[90dvh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="sibling-propagate-modal">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif-display text-lg text-[#1C1B1A] flex items-center gap-2">
            <ArrowsClockwise size={16} weight="duotone" className="text-[#8A6AC1]" />
            Apply to siblings?
          </h2>
          <button onClick={onClose} disabled={busy} className="p-1 rounded hover:bg-[#F0F1EE] disabled:opacity-40" data-testid="sibling-propagate-close">
            <X size={16} />
          </button>
        </div>
        <p className="text-sm text-[#1C1B1A] mb-3 leading-relaxed">
          You changed <strong>&ldquo;{title}&rdquo;</strong>
          {oldDowName && newDowName && oldDowName !== newDowName && (
            <> from <strong>{oldDowName} {prettyDate(priorEvent?.start_date)}</strong> to <strong>{newDowName} {prettyDate(newEvent?.start_date)}</strong></>
          )}.
          There {count === 1 ? "is 1 other future" : `are ${count} other future`} matching event{count === 1 ? "" : "s"}. How should I handle them?
        </p>

        <div className="space-y-2" data-testid="sibling-propagate-modes">
          <ModeRadio value="just_this" label="Just this one (leave siblings alone)" mode={mode} setMode={setMode} testid="mode-just-this" />
          {(delta.dateShift !== 0 || 0) && (
            <ModeRadio value="shift_by" label={`Shift them all by ${delta.dateShift > 0 ? "+" : ""}${delta.dateShift} day${Math.abs(delta.dateShift) === 1 ? "" : "s"} (${oldDowName} → ${newDowName})`} mode={mode} setMode={setMode} testid="mode-shift-by" />
          )}
          {delta.dateShift !== 0 && (
            <ModeRadio value="new_pattern" label={`Move them to the new day of the week (all → ${newDowName}s)`} mode={mode} setMode={setMode} testid="mode-new-pattern" />
          )}
          <ModeRadio value="biweekly" label="Change to biweekly (keep every other, delete the rest)" mode={mode} setMode={setMode} testid="mode-biweekly" />
          <ModeRadio value="monthly" label="Change to monthly (keep only the first each month)" mode={mode} setMode={setMode} testid="mode-monthly" />
          <ModeRadio value="every_n_days" label={
            <span className="flex items-center gap-1.5">
              Every
              <input
                type="number"
                min="1"
                max="365"
                value={nDays}
                onChange={(e) => setNDays(Math.max(1, Math.min(365, parseInt(e.target.value || "1", 10))))}
                className="w-14 px-1 py-0.5 border hairline rounded text-sm"
                data-testid="mode-every-n-days-input"
                onClick={(e) => { e.stopPropagation(); setMode("every_n_days"); }}
              />
              days
            </span>
          } mode={mode} setMode={setMode} testid="mode-every-n-days" />
          <ModeRadio value="custom" label={
            <span className="flex items-center gap-1.5 flex-wrap">
              Custom range
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setMode("custom"); }}
                className="px-1 py-0.5 border hairline rounded text-xs"
                data-testid="mode-custom-from"
                onClick={(e) => e.stopPropagation()}
              />
              <ArrowRight size={11} weight="bold" className="text-[#6B6A68]" />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setMode("custom"); }}
                className="px-1 py-0.5 border hairline rounded text-xs"
                data-testid="mode-custom-to"
                onClick={(e) => e.stopPropagation()}
              />
            </span>
          } mode={mode} setMode={setMode} testid="mode-custom" />
        </div>

        {mode !== "just_this" && (
          <div className="mt-4 pt-3 border-t hairline">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#6B6A68] mb-1.5">Also propagate:</div>
            {delta.timeChanged && (
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={applyTime} onChange={(e) => setApplyTime(e.target.checked)} data-testid="propagate-apply-time" />
                Time ({priorEvent?.start_time || "—"} → {newEvent?.start_time || "—"})
              </label>
            )}
            {delta.durationChanged && (
              <label className="flex items-center gap-2 text-xs cursor-pointer mt-1">
                <input type="checkbox" checked={applyDuration} onChange={(e) => setApplyDuration(e.target.checked)} data-testid="propagate-apply-duration" />
                Duration ({priorEvent?.end_time || "—"} → {newEvent?.end_time || "—"})
              </label>
            )}
            {delta.assigneeChanged && (
              <label className="flex items-center gap-2 text-xs cursor-pointer mt-1">
                <input type="checkbox" checked={applyAssignee} onChange={(e) => setApplyAssignee(e.target.checked)} data-testid="propagate-apply-assignee" />
                Assignee
              </label>
            )}
            {!delta.timeChanged && !delta.durationChanged && !delta.assigneeChanged && (
              <div className="text-[10px] text-[#6B6A68] italic">Only the date/pattern will propagate.</div>
            )}
          </div>
        )}

        {result && (
          <div className={`mt-3 text-xs p-2 rounded-md border ${
            result.kind === "ok" ? "border-[#4A9E6A] bg-[#E9F5EC] text-[#1F5D31]" : "border-[#C16E5A] bg-[#FBEBE4] text-[#8A3A28]"
          }`} data-testid="sibling-propagate-result">
            {result.msg}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-4">
          <button onClick={onClose} disabled={busy} className="text-xs px-3 py-2 rounded text-[#6B6A68] hover:bg-[#F0F1EE] disabled:opacity-40" data-testid="sibling-propagate-cancel">
            {result?.kind === "ok" ? "Done" : "Cancel"}
          </button>
          {result?.kind !== "ok" && (
            <button
              onClick={apply}
              disabled={busy}
              data-testid="sibling-propagate-apply"
              className="text-xs px-4 py-2 rounded-md bg-[#8A6AC1] text-white hover:bg-[#7A5AB1] disabled:opacity-40 flex items-center gap-1"
            >
              {busy ? "Applying…" : mode === "just_this" ? "Keep as-is" : "Apply"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ModeRadio({ value, label, mode, setMode, testid }) {
  return (
    <label
      className={`flex items-start gap-2 p-2 rounded cursor-pointer hover:bg-[#F8F5FC] border ${mode === value ? "border-[#8A6AC1] bg-[#F0EAF7]" : "border-transparent"}`}
      data-testid={testid}
    >
      <input
        type="radio"
        checked={mode === value}
        onChange={() => setMode(value)}
        className="mt-0.5"
      />
      <div className="text-sm text-[#1C1B1A] flex-1">{label}</div>
    </label>
  );
}
